import { onError } from "@elsa-zku/lib/helpers/fetch";
import * as fetch from "@elsa-zku/lib/helpers/fetch";
import { action, map } from 'nanostores';
import { useStore, UseStoreOptions } from "@nanostores/react";
import * as ws from "@elsa-zku/lib/helpers/websockets";
import { getCurrentUser } from "src/api/currentUser";
import { Company } from "src/api/data-contracts";

export enum UserRole {
  ROLE_USER = 'ROLE_USER',
  ROLE_ADMIN = 'ROLE_ADMIN',
}

export type UserStore = {
  isAuth: boolean,
  email: string,
  roles: Record<UserRole, true>,
}

const prepareRoles = (roles: UserRole[]) => roles.reduce((dict, role) => {
  dict[role] = true;
  return dict;
}, {} as Record<UserRole, true>)

export const userStore = map<UserStore>({
  isAuth: null,
  email:  null,
  roles:  null,
})

export const auth = action(userStore, 'auth', (store) => {
  const token: string | null = localStorage.token;
  if (!token) {
    return unauth();
  }

  setToken(token);
  getCurrentUser().then(
    ({ user, role, company }) => {
      setToken(token);
      store.set({ isAuth: true, email: user, roles: prepareRoles(role as UserRole[]) });
    },
    () => unauth(),
  );
});

export const unauth = action(userStore, 'unauth', (store) => {
  store.set({ isAuth: false, email: null, roles: null });
  setToken(null);
});

export const login = action(userStore, 'login', (store, email: string, token: string, roles: UserRole[], company: Company) => {
  setToken(token);
  store.set({ isAuth: true, email, roles: prepareRoles(roles) });
})

export type UseUserOptions = UseStoreOptions<typeof userStore>;
export const useUser = (options?: UseUserOptions) => useStore(userStore, options);

const setToken = (accessToken: string) => {
  if (accessToken) {
    localStorage.token = accessToken;
  } else {
    delete localStorage.token;
  }
  ws.setToken(accessToken);
  fetch.setToken(accessToken);
}

const useIsAdminOptions: UseUserOptions = { keys: ['roles'] }
export const useIsAdmin = () => !!useUser(useIsAdminOptions).roles[UserRole.ROLE_ADMIN];

onError((e) => {
  if (e.status === 401) {
    unauth();
  }
})
